import React from 'react';
import { cn } from '../../lib/utils';

interface ViewModeButtonProps {
  /** The current view mode (used to determine button appearance) */
  currentMode: string;

  /** The mode this button represents */
  mode: string;

  /** Click handler for the button */
  onClick: () => void;

  /** Text label to display */
  label: string;

  /** Hide label */
  noLabel: boolean;

  /** Icon to display */
  icon: React.ReactNode;

  /** Whether the button is being rendered in a mobile context */
  isMobile?: boolean;

  /** Additional CSS class names to apply to the button */
  className?: string;

  /** Optional ref for the button element */
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * A standardized button for switching between different view modes (e.g. board/timeline, grid/list).
 * Uses consistent styling similar to FilterButton but with inverted colors when active.
 */
export function ViewModeButton({
  currentMode,
  mode,
  onClick,
  label,
  noLabel,
  icon,
  isMobile = false,
  className = '',
  buttonRef,
}: ViewModeButtonProps) {
  const isActive = currentMode === mode;

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      title={label}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm transition-colors items-center gap-2',
        isActive
          ? 'bg-transparent text-blue-700 border border-blue-200 hover:bg-white'
          : 'bg-white text-gray-700 hover:bg-white hover:text-blue-700 hover:shadow-sm',
        isMobile ? 'text-xs' : 'text-sm',
        className
      )}
    >
      {icon}
      {!noLabel && (
        <span className="whitespace-nowrap">
          {isMobile && label.length > 8 ? label.split(' ')[0] : label}
        </span>
      )}
    </button>
  );
}
